import { Link } from "wouter";
import { UtensilsCrossed } from "lucide-react";
import { cn } from "@/lib/utils";
import { TOUCH_BG } from "@/components/dashboard/widgets/opsColors";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import { SPECIALTY_LABELS } from "@/components/coi/specialty";
import { MEAL_LABEL } from "@shared/meals";
import { BD_LOG_LIMIT, type BdLogKind, type BdPayload } from "@shared/bdTracker";
import { centralDay, firstName } from "./format";

const KIND_LABEL: Record<BdLogKind, string> = {
  meeting: "Meeting", call: "Call", email: "Email", coi_added: "Added COI", sourced: "Sourced", referral_in: "Referral in", referral_sent: "Referral sent",
};
/** Everything the selected person got done in the period, newest first. */
export function BdActivityLog({ data }: { data: BdPayload }) {
  const person = data.people.find((p) => p.id === data.who);
  const when = data.period.period === "week" ? "this week" : `in ${data.period.label}`;
  return (
    <WidgetPanel title="Activity log" meta={person ? person.name : "everyone"}>
      {data.log.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">{`Nothing logged ${when} yet.`}</p>
      ) : (
        <ul>
          {data.log.map((i) => {
            const sub = [i.sub, i.specialty ? SPECIALTY_LABELS[i.specialty] ?? i.specialty : null].filter(Boolean).join(" · ");
            return (
              <li key={i.key} className="flex gap-2 items-baseline px-3 py-1.5 border-b border-border/40 text-xs">
                <span className="w-11 shrink-0 text-muted-foreground tabular-nums">{centralDay(i.at)}</span>
                <span className="min-w-0 flex-1">
                  {(i.kind === "meeting" || i.kind === "call" || i.kind === "email") && (
                    <span aria-hidden className={cn("inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle", TOUCH_BG[i.kind])} />
                  )}
                  <span className="inline-block text-[9px] font-bold uppercase tracking-wide px-1 rounded mr-1.5 align-[1px] bg-muted text-muted-foreground">
                    {i.meal ? MEAL_LABEL : KIND_LABEL[i.kind]}
                  </span>
                  {i.meal && <UtensilsCrossed aria-hidden className="inline w-3 h-3 mr-1 text-muted-foreground" />}
                  {i.href ? <Link href={i.href} className="hover:underline">{i.title}</Link> : i.title}
                  {sub && <span className="block truncate text-[11px] text-muted-foreground">{sub}</span>}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{i.by.map(firstName).join(", ")}</span>
              </li>
            );
          })}
        </ul>
      )}
      {data.log.length >= BD_LOG_LIMIT && (
        <p className="px-3 py-2 text-[11px] text-muted-foreground">{`Showing the latest ${BD_LOG_LIMIT}.`}</p>
      )}
    </WidgetPanel>
  );
}
